import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { Edit, Check, Close, ContentCopy } from '@mui/icons-material';

const getCopyText = (value, displayValue) => {
  const raw = value ?? displayValue;
  if (raw === null || raw === undefined || raw === '' || raw === '—') {
    return '';
  }
  return String(raw);
};

const CopyableValue = ({ text, shownValue, onCopy, copied }) => {
  const canCopy = Boolean(text);

  const handleCopy = (e) => {
    e.stopPropagation();
    if (canCopy) onCopy(e);
  };

  return (
    <Tooltip title={copied ? 'Skopiowano!' : (canCopy ? 'Kliknij, aby skopiować' : '')}>
      <Typography
        variant="body2"
        onClick={handleCopy}
        sx={{
          flex: 1,
          wordBreak: 'break-word',
          pt: 0.15,
          fontWeight: 600,
          color: 'text.primary',
          cursor: canCopy ? 'pointer' : 'default',
          borderRadius: 0.5,
          transition: 'color 0.15s',
          ...(canCopy && {
            '&:hover': { color: 'primary.main' },
          }),
        }}
      >
        {shownValue || '—'}
      </Typography>
    </Tooltip>
  );
};

const FieldActions = ({
  canCopy,
  copied,
  onCopy,
  showEdit,
  editButton,
}) => (
  <Box sx={{ display: 'flex', flexShrink: 0, alignItems: 'flex-start', gap: 0.25 }}>
    {canCopy && (
      <Tooltip title={copied ? 'Skopiowano!' : 'Kopiuj'}>
        <IconButton
          size="small"
          onClick={onCopy}
          aria-label="Kopiuj"
          color={copied ? 'success' : 'default'}
        >
          {copied ? <Check sx={{ fontSize: 15 }} /> : <ContentCopy sx={{ fontSize: 15 }} />}
        </IconButton>
      </Tooltip>
    )}
    {showEdit && editButton}
  </Box>
);

export const ReadOnlyDetailItem = ({ label, value, compact = false }) => {
  const [copied, setCopied] = useState(false);
  const shownValue = value || '—';
  const copyText = getCopyText(value, null);

  const handleCopy = async (e) => {
    e.stopPropagation();
    if (!copyText) return;

    try {
      await navigator.clipboard.writeText(copyText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Błąd podczas kopiowania:', err);
    }
  };

  if (compact) {
    return (
      <Box
        className="field-row"
        sx={{ display: 'flex', gap: 1, py: 0.75, alignItems: 'flex-start' }}
        onClick={(e) => e.stopPropagation()}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ width: 130, flexShrink: 0, fontSize: '0.8125rem', fontWeight: 500, pt: 0.15 }}
        >
          {label}
        </Typography>
        <CopyableValue
          text={copyText}
          shownValue={shownValue}
          onCopy={handleCopy}
          copied={copied}
        />
        <FieldActions canCopy={Boolean(copyText)} copied={copied} onCopy={handleCopy} showEdit={false} />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mb: 0.25, fontWeight: 500 }}
      >
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <CopyableValue
          text={copyText}
          shownValue={shownValue}
          onCopy={handleCopy}
          copied={copied}
        />
        <FieldActions canCopy={Boolean(copyText)} copied={copied} onCopy={handleCopy} showEdit={false} />
      </Box>
    </Box>
  );
};

const EditableDetailItem = ({
  field,
  label,
  displayValue,
  value,
  inputType = 'text',
  selectOptions = {},
  editingField,
  savingField,
  onStartEdit,
  onCancelEdit,
  onSave,
  editable = true,
  compact = false,
}) => {
  const [draft, setDraft] = useState(value ?? '');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const isEditing = editingField === field;
  const isSaving = savingField === field;
  const shownValue = displayValue ?? value ?? '—';
  const isMultiline = inputType === 'multiline';
  const copyText = inputType === 'date'
    ? getCopyText(String(displayValue).replace(/\s*\([^)]*\)$/, '').trim(), null)
    : inputType === 'select'
      ? getCopyText(displayValue ?? value, null)
      : getCopyText(value, displayValue);

  useEffect(() => {
    if (isEditing) {
      setDraft(value ?? '');
    }
    setError('');
  }, [isEditing, value]);

  const handleCopy = async (e) => {
    e.stopPropagation();
    if (!copyText) return;

    try {
      await navigator.clipboard.writeText(copyText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Błąd podczas kopiowania:', err);
    }
  };

  const handleStart = (e) => {
    e.stopPropagation();
    if (!editable || isSaving) return;
    onStartEdit(field);
  };

  const handleCancel = (e) => {
    e.stopPropagation();
    setDraft(value ?? '');
    setError('');
    onCancelEdit();
  };

  const handleSave = async (e) => {
    e.stopPropagation();

    const result = await onSave(field, draft);
    if (!result.success) {
      setError(result.error || 'Nie udało się zapisać.');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !isMultiline) {
      e.preventDefault();
      handleSave(e);
    }
    if (e.key === 'Escape') {
      handleCancel(e);
    }
  };

  const editButton = (
    <Tooltip title="Edytuj">
      <IconButton
        size="small"
        onClick={handleStart}
        disabled={Boolean(editingField) || isSaving}
        aria-label={`Edytuj ${label}`}
      >
        <Edit sx={{ fontSize: 15 }} />
      </IconButton>
    </Tooltip>
  );

  if (!editable) {
    return (
      <ReadOnlyDetailItem label={label} value={shownValue === '—' ? '' : shownValue} compact={compact} />
    );
  }

  if (isEditing) {
    return (
      <Box
        sx={{ py: compact ? 0.75 : 0, mb: compact ? 0 : 1.5 }}
        onClick={(e) => e.stopPropagation()}
      >
        {compact && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 0.75, fontSize: '0.8125rem', fontWeight: 500 }}
          >
            {label}
          </Typography>
        )}
        {!compact && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
            {label}
          </Typography>
        )}

        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
          {inputType === 'select' ? (
            <FormControl size="small" fullWidth error={Boolean(error)}>
              <InputLabel>{label}</InputLabel>
              <Select
                value={draft}
                label={label}
                onChange={(e) => {
                  setDraft(e.target.value);
                  setError('');
                }}
                autoFocus
              >
                {Object.entries(selectOptions).map(([optionValue, optionLabel]) => (
                  <MenuItem key={optionValue} value={optionValue}>
                    {optionLabel}
                  </MenuItem>
                ))}
              </Select>
              {error && <FormHelperText>{error}</FormHelperText>}
            </FormControl>
          ) : (
            <TextField
              size="small"
              fullWidth
              autoFocus
              type={inputType === 'email' ? 'email' : inputType === 'date' ? 'date' : 'text'}
              multiline={isMultiline}
              minRows={isMultiline ? 2 : undefined}
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                setError('');
              }}
              onKeyDown={handleKeyDown}
              error={Boolean(error)}
              helperText={error || (inputType === 'date' ? 'Wyczyść pole, aby użyć daty z ostatniego komentarza' : undefined)}
              placeholder={label}
              InputLabelProps={inputType === 'date' ? { shrink: true } : undefined}
            />
          )}

          <Box sx={{ display: 'flex', flexShrink: 0, pt: inputType === 'select' ? 0.5 : 0 }}>
            <Tooltip title="Zapisz">
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={handleSave}
                  disabled={isSaving}
                  aria-label="Zapisz"
                >
                  {isSaving ? <CircularProgress size={16} /> : <Check fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Anuluj">
              <span>
                <IconButton
                  size="small"
                  onClick={handleCancel}
                  disabled={isSaving}
                  aria-label="Anuluj"
                >
                  <Close fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    );
  }

  if (compact && !isMultiline) {
    return (
      <Box
        className="field-row"
        sx={{ display: 'flex', gap: 1, py: 0.75, alignItems: 'flex-start' }}
        onClick={(e) => e.stopPropagation()}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ width: 130, flexShrink: 0, fontSize: '0.8125rem', fontWeight: 500, pt: 0.15 }}
        >
          {label}
        </Typography>
        <CopyableValue
          text={copyText}
          shownValue={shownValue}
          onCopy={handleCopy}
          copied={copied}
        />
        <FieldActions
          canCopy={Boolean(copyText)}
          copied={copied}
          onCopy={handleCopy}
          showEdit
          editButton={editButton}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 1.5 }} onClick={(e) => e.stopPropagation()}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, minHeight: 28 }}>
        <CopyableValue
          text={copyText}
          shownValue={shownValue}
          onCopy={handleCopy}
          copied={copied}
        />
        <FieldActions
          canCopy={Boolean(copyText)}
          copied={copied}
          onCopy={handleCopy}
          showEdit
          editButton={editButton}
        />
      </Box>
    </Box>
  );
};

export default EditableDetailItem;
